/**
 * 换牌弹窗 - 从牌堆中选择卡牌加入手牌
 * 
 * 玩家每局游戏最多进行 MAX_SWAPS 次换牌，
 * 每次可从牌堆中选择 1～5 张牌加入手牌。
 */

import React from 'react';
import { CardItem } from '../cards/CardItem';
import type { GameCard } from '../../../types';
import { MAX_HAND_SIZE, MAX_SWAPS } from '../../../constants';

interface SwapCardsModalProps {
  isOpen: boolean;
  deckCards: GameCard[];
  handSize: number;
  swapCount: number;
  selectedIds: Set<string>;
  selectedCount: number;
  canConfirm: boolean;
  onToggleCard: (card: GameCard) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

const MAX_SWAP_SELECT = 5;

export const SwapCardsModal: React.FC<SwapCardsModalProps> = ({
  isOpen,
  deckCards,
  handSize,
  swapCount,
  selectedIds,
  selectedCount,
  canConfirm,
  onToggleCard,
  onConfirm,
  onCancel,
}) => {
  if (!isOpen || deckCards.length === 0) return null;

  const totalAfterSwap = handSize + selectedCount;
  const willNeedDiscard = totalAfterSwap > MAX_HAND_SIZE;
  const swapsRemaining = MAX_SWAPS - swapCount;

  // Filter out cards that are already in hand (shown in deck but not selectable if in hand)
  // Note: deck cards not in hand are the ones available to pick

  return (
    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#FFF8F0] via-white to-[#FFF8F0] p-5 border-b border-[#E5E0D8] shrink-0">
          <h3 className="text-xl font-bold text-[#2D2D2D] text-center">从牌堆中选择卡牌</h3>
          <p className="text-sm text-[#6B7280] text-center mt-2">
            选择 1～{MAX_SWAP_SELECT} 张卡牌加入手牌
            <span className="ml-2 px-2 py-0.5 bg-[#9B8EC4]/10 text-[#9B8EC4] rounded-full text-xs font-medium">
              剩余换牌次数: {swapsRemaining}/{MAX_SWAPS}
            </span>
          </p>
        </div>

        {/* Cards Grid */}
        <div className="flex-1 overflow-y-auto p-4 bg-[#FAFAFA]">
          {deckCards.length === 0 ? (
            <div className="text-center text-[#9CA3AF] py-12">
              牌堆中没有可选的卡牌
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 max-w-3xl mx-auto">
              {deckCards.map((card) => (
                <CardItem
                  key={card.id}
                  card={card}
                  isSelected={selectedIds.has(card.id)}
                  selectionMode="select"
                  onClick={() => onToggleCard(card)}
                  showDescription
                  showHint
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gradient-to-r from-[#FFF8F0] via-white to-[#FFF8F0] p-4 border-t border-[#E5E0D8] shrink-0">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
            <div className="text-sm">
              <span className="text-[#6B7280]">已选择: </span>
              <span className={`font-bold ${selectedCount > 0 ? 'text-[#9B8EC4]' : 'text-[#2D2D2D]'}`}>
                {selectedCount}/{MAX_SWAP_SELECT}
              </span>
              <span className="text-[#6B7280] ml-4">当前手牌: </span>
              <span className="font-bold text-[#2D2D2D]">
                {handSize}/{MAX_HAND_SIZE}
              </span>
              {willNeedDiscard && (
                <span className="text-red-500 text-xs ml-2">
                  (需弃掉 {totalAfterSwap - MAX_HAND_SIZE} 张)
                </span>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={onCancel}
                className="px-6 py-3 rounded-xl font-bold text-[#6B7280] border border-[#E5E0D8] hover:bg-[#F5EDE3] transition-all"
              >
                取消
              </button>
              <button
                onClick={onConfirm}
                disabled={!canConfirm}
                className={`
                  px-8 py-3 rounded-xl font-bold text-white transition-all
                  ${canConfirm
                    ? 'bg-gradient-to-r from-[#9B8EC4] to-[#8A7DB3] shadow-lg hover:shadow-xl hover:-translate-y-0.5'
                    : 'bg-[#E5E0D8] cursor-not-allowed'
                  }
                `}
              >
                确认换牌
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
